import { useMemo, useState } from 'react';

import { IconCode, IconList } from '@u21/tabler-icons';
import { RuleAdminSidebarDefinition } from 'app/modules/sidebar/models';
import {
  U21Button,
  U21JsonViewer,
  U21TextField,
  U21Tooltip,
  U21Typography,
} from 'app/shared/u21-ui/components';
import { U21SideMenu } from 'app/shared/u21-ui/components/dashboard';
import { TableConfig } from 'app/shared/pagination/models';
import { RULE_ADMIN_INFO_MAP } from 'app/modules/rulesAdmin/models';
import styled from 'styled-components';
import { CopyButton } from 'app/modules/rulesAdmin/components/sidebar/CopyButton';

enum ParsedValueType {
  STRING_ARRAY = 'STRING_ARRAY',
  JSON = 'JSON',
}

type ParsedValue =
  | { type: ParsedValueType.STRING_ARRAY; data: string[] }
  | { type: ParsedValueType.JSON; data: object }
  | null;

// Attempts to parse a JSON string into a typed structure for pretty rendering.
// Returns null for non-JSON values, which fall back to a raw textarea.
const parseStructuredValue = (value: unknown): ParsedValue => {
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')) {
      return { type: ParsedValueType.STRING_ARRAY, data: parsed };
    }
    if (typeof parsed === 'object' && parsed !== null) {
      return { type: ParsedValueType.JSON, data: parsed };
    }
    return null;
  } catch {
    return null;
  }
};

interface SidebarFieldProps {
  label: string;
  value: string;
}

const SidebarField = ({ label, value }: SidebarFieldProps) => {
  const parsed = useMemo(() => parseStructuredValue(value), [value]);
  const [showRaw, setShowRaw] = useState(false);

  const prettyContent = useMemo(() => {
    if (!parsed) return null;
    if (parsed.type === ParsedValueType.STRING_ARRAY) {
      if (parsed.data.length === 0) {
        return (
          <U21Typography color="text.secondary" variant="body2">
            No items
          </U21Typography>
        );
      }
      return (
        <StyledList>
          {parsed.data.map((item) => (
            <li key={item}>
              <U21Typography variant="body2">{item}</U21Typography>
            </li>
          ))}
        </StyledList>
      );
    }
    return (
      <U21JsonViewer
        data={parsed.data}
        defaultInspectDepth={Infinity}
        displayDataTypes={false}
      />
    );
  }, [parsed]);

  const showPretty = parsed && !showRaw;

  const rawContent = (
    <U21TextField type="textarea" disabled onChange={() => {}} value={value} />
  );

  const toggleButton = (
    <U21Tooltip tooltip={showRaw ? 'Pretty view' : 'Raw view'}>
      <U21Button
        aria-label="Toggle view"
        icon={showRaw ? <IconList /> : <IconCode />}
        onClick={() => setShowRaw((prev) => !prev)}
        size="small"
      />
    </U21Tooltip>
  );

  return (
    <div>
      <StyledTitleBar>
        <U21Typography variant="subtitle2">{label}</U21Typography>
        <ActionRow>
          <CopyButton content={value} />
          {parsed && toggleButton}
        </ActionRow>
      </StyledTitleBar>
      {showPretty ? prettyContent : rawContent}
    </div>
  );
};

interface OwnProps {
  data: RuleAdminSidebarDefinition['data'];
}

export const SidebarRuleAdmin = ({ data }: OwnProps) => {
  return (
    <U21SideMenu
      title={RULE_ADMIN_INFO_MAP[data.contentType].title}
      titleIcon={RULE_ADMIN_INFO_MAP[data.contentType].icon}
    >
      {data.contentHeader.map((header: TableConfig) => (
        <SidebarField
          key={header.key}
          label={header.label}
          value={data.content[header.key]}
        />
      ))}
    </U21SideMenu>
  );
};

const StyledTitleBar = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
`;

const StyledList = styled.ul`
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing(2)};
`;
